import { useEffect, useState } from "react";
import ModalSheet from "./ModalSheet";
import { FONT, COLOR, RADIUS, Z } from "../lib/tokens";
import { useToast } from "../lib/toast";
import {
  getRecipeShareByToken,
  getRecipeShareById,
  acceptRecipeShare,
  declineRecipeShare,
} from "../lib/recipeShares";
import { totalTimeMin } from "../data/recipes";

// IncomingShareSheet — someone sent the user a booda.app/r/<token>
// recipe link. Preview what was shared + who sent it; ACCEPT saves an
// independent copy to the viewer's own book (the share carries a
// snapshot, so the sender editing or deleting theirs never touches it).
//
// Reached via: universal link tap (native), /r/<token> path (web), the
// cold-start stash in App.jsx, or a direct-share notification tap
// (target_kind 'recipe_share' → shareId; RLS lets the named recipient
// read the row, respond_recipe_share answers by id). The share stays
// claimable until someone accepts it, so re-opening later still works.

export default function IncomingShareSheet({ token, shareId, saveRecipe, onClose, onAccepted }) {
  const { push: pushToast } = useToast();
  const [share, setShare] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | ready | gone | busy

  useEffect(() => {
    let alive = true;
    (async () => {
      const row = token
        ? await getRecipeShareByToken(token)
        : await getRecipeShareById(shareId);
      if (!alive) return;
      if (!row || row.status === "declined") { setPhase("gone"); return; }
      // Carry the token — respond_recipe_share claims open link shares
      // by token, and the RPC's return shape doesn't echo it. Direct
      // shares (shareId path) answer by row id instead.
      setShare(token ? { ...row, token } : row);
      setPhase(row.status === "accepted" ? "gone" : "ready");
    })();
    return () => { alive = false; };
  }, [token, shareId]);

  const accept = async () => {
    if (phase === "busy" || !share) return;
    setPhase("busy");
    try {
      const saved = await acceptRecipeShare(share, saveRecipe);
      pushToast(`"${share.recipe_title || "Recipe"}" added to your book`, { emoji: "📖", kind: "info" });
      onAccepted?.(saved);
      onClose?.();
    } catch (e) {
      console.error("[incomingShare] accept failed:", e);
      pushToast(e?.message || "Couldn't save that — try again.", { emoji: "⚠️", kind: "warn" });
      setPhase("ready");
    }
  };

  const decline = () => {
    // Fire-and-forget — closing is the user-visible action.
    if (share) declineRecipeShare(share).catch(() => {});
    onClose?.();
  };

  const snapshot = share?.recipe_snapshot || null;
  const ingredientCount = Array.isArray(snapshot?.ingredients) ? snapshot.ingredients.length : 0;
  const minutes = snapshot ? totalTimeMin(snapshot) : 0;

  return (
    <ModalSheet onClose={onClose} zIndex={Z.sheet} label="RECIPE SHARED WITH YOU">
      {phase === "loading" && (
        <div style={{ fontFamily: FONT.forma, fontSize: 14, color: COLOR.muted, padding: "24px 0", textAlign: "center" }}>
          Opening the recipe…
        </div>
      )}

      {phase === "gone" && (
        <div style={{ padding: "8px 0 16px" }}>
          <div style={{ fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500, fontSize: 22, color: COLOR.ink, marginBottom: 8 }}>
            This share link isn't available
          </div>
          <div style={{ fontFamily: FONT.forma, fontSize: 13, color: COLOR.muted, lineHeight: 1.5 }}>
            It may have already been claimed, or the link is incomplete.
            Ask the sender to share it again.
          </div>
        </div>
      )}

      {(phase === "ready" || phase === "busy") && share && (
        <div style={{ padding: "4px 0 8px" }}>
          {snapshot?.goalImageURL && (
            <img
              src={snapshot.goalImageURL}
              alt=""
              style={{
                width: "100%", aspectRatio: "16 / 9", objectFit: "cover",
                borderRadius: RADIUS.xl, marginBottom: 12,
              }}
            />
          )}
          <div style={{ fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500, fontSize: 24, color: COLOR.ink, lineHeight: 1.15, marginBottom: 4 }}>
            {share.recipe_title || snapshot?.title || "A recipe"}
          </div>
          {snapshot?.subtitle && (
            <div style={{ fontFamily: FONT.forma, fontSize: 13, color: COLOR.muted, marginBottom: 6 }}>
              {snapshot.subtitle}
            </div>
          )}
          <div style={{ fontFamily: FONT.forma, fontSize: 13, color: COLOR.sub, marginBottom: 16 }}>
            {share.sender_name ? `From ${share.sender_name} · ` : ""}
            {ingredientCount} ingredient{ingredientCount === 1 ? "" : "s"}
            {minutes ? ` · ${minutes} min` : ""}
          </div>

          <button
            className="mcm-focusable"
            onClick={accept}
            disabled={phase === "busy"}
            style={{
              width: "100%",
              fontFamily: FONT.forma, fontSize: 14, fontWeight: 600,
              letterSpacing: "0.12em", color: COLOR.ink,
              background: COLOR.goldDeep, border: `1px solid ${COLOR.goldDim}`,
              borderRadius: RADIUS.xl, padding: "14px 16px",
              cursor: phase === "busy" ? "wait" : "pointer",
              opacity: phase === "busy" ? 0.6 : 1,
            }}
          >
            {phase === "busy" ? "SAVING…" : "ADD TO MY RECIPE BOOK"}
          </button>
          <button
            onClick={decline}
            style={{
              width: "100%", background: "transparent", border: "none",
              fontFamily: FONT.forma, fontSize: 13, color: COLOR.sub,
              letterSpacing: "0.08em", padding: "12px 0", cursor: "pointer",
            }}
          >
            NO THANKS
          </button>
        </div>
      )}
    </ModalSheet>
  );
}
